import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Tooltip, IconButton, Button } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import useThemeStyles from '../../../hooks/useThemeStyles';

const ActivityCalendar = ({ activity, dailyWordGoal = 500 }) => {
  const { colors } = useThemeStyles();
  const today = useMemo(() => new Date(), []);
  const [currentDate, setCurrentDate] = useState(today);

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  // Create a map of activity by date string (YYYY-MM-DD)
  const activityMap = useMemo(() => {
    const map = new Map();
    if (activity && activity.forEach) {
      activity.forEach((entry) => {
        const date = new Date(entry.date);
        const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        map.set(dateKey, entry.wordCount || 0);
      });
    }
    return map;
  }, [activity]);

  // Calculate streak and days goal met for current month
  const { streak, daysGoalMet } = useMemo(() => {
    let currentStreak = 0;
    let goalMetCount = 0;
    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const todayCount = activityMap.get(todayKey) || 0;

    // Check if today's goal is met
    if (todayCount >= dailyWordGoal) {
      currentStreak = 1;
    }

    // Check previous days for streak
    for (let i = 1; i <= 365; i += 1) {
      const checkDate = new Date(today);
      checkDate.setDate(checkDate.getDate() - i);
      const dateKey = `${checkDate.getFullYear()}-${String(checkDate.getMonth() + 1).padStart(2, '0')}-${String(checkDate.getDate()).padStart(2, '0')}`;
      const wordCount = activityMap.get(dateKey) || 0;

      if (wordCount >= dailyWordGoal) {
        currentStreak += 1;
      } else {
        break;
      }
    }

    // Count days goal met in current month
    const monthDays = new Date(currentYear, currentMonth + 1, 0).getDate();
    for (let day = 1; day <= monthDays; day += 1) {
      const dateKey = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const wordCount = activityMap.get(dateKey) || 0;
      if (wordCount >= dailyWordGoal) {
        goalMetCount += 1;
      }
    }

    return { streak: currentStreak, daysGoalMet: goalMetCount };
  }, [activityMap, dailyWordGoal, currentYear, currentMonth, today]);

  // Get days in month
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

  // Get color for a day based on word count
  const getDayColor = (wordCount) => {
    if (wordCount === 0) return colors.background.secondary || '#e5e5e5';
    if (wordCount >= dailyWordGoal) return '#4caf50'; // Green for goal met
    return '#ffc107'; // Yellow for partial activity
  };

  // Navigation handlers
  const handlePreviousMonth = () => {
    const newDate = new Date(currentYear, currentMonth - 1, 1);
    setCurrentDate(newDate);
  };

  const handleNextMonth = () => {
    // Don't allow navigating to future months
    const nextMonth = new Date(currentYear, currentMonth + 1, 1);
    const todayMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    if (nextMonth <= todayMonth) {
      setCurrentDate(nextMonth);
    }
  };

  // Check if we can navigate to next month
  const canGoNext = useMemo(() => {
    const nextMonth = new Date(currentYear, currentMonth + 1, 1);
    const todayMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return nextMonth <= todayMonth;
  }, [currentYear, currentMonth, today]);

  // Check if we're viewing the current month
  const isCurrentMonth = currentYear === today.getFullYear() && currentMonth === today.getMonth();

  // Handler to jump to current month
  const handleGoToToday = () => {
    setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  // Generate calendar grid
  const calendarDays = [];

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDayOfMonth; i += 1) {
    calendarDays.push(null);
  }

  // Add cells for each day of the month
  for (let day = 1; day <= daysInMonth; day += 1) {
    const dateKey = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const wordCount = activityMap.get(dateKey) || 0;
    // Check if this day is today (works across any month)
    const isToday =
      day === today.getDate() &&
      currentMonth === today.getMonth() &&
      currentYear === today.getFullYear();

    calendarDays.push({
      day,
      wordCount,
      isToday,
      dateKey,
    });
  }

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  return (
    <Box id="activity-calendar">
      {/* Summary Box */}
      <Box
        id="calendar-summary"
        sx={{
          backgroundColor: '#f0f9ff',
          borderRadius: '8px',
          padding: 2,
          marginBottom: 3,
          border: '1px solid #e0f2fe',
        }}
      >
        <Typography
          id="calendar-summary-title"
          variant="body2"
          sx={{ fontWeight: 500, marginBottom: 0.5 }}
        >
          {monthNames[currentMonth]} Summary
        </Typography>
        <Typography
          id="calendar-summary-text"
          variant="body2"
          sx={{ color: colors.text.secondary }}
        >
          {daysGoalMet} day{daysGoalMet !== 1 ? 's' : ''} goal met
          {currentYear === today.getFullYear() &&
            currentMonth === today.getMonth() &&
            streak > 0 &&
            ` • ${streak}-day streak`}
        </Typography>
      </Box>

      {/* Calendar Navigation */}
      <Box
        id="calendar-navigation"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 2,
        }}
      >
        <IconButton
          id="calendar-previous-month-button"
          onClick={handlePreviousMonth}
          size="small"
          sx={{
            color: colors.text.primary,
            '&:hover': {
              backgroundColor: colors.background.secondary,
            },
          }}
          aria-label="Previous month"
        >
          <ChevronLeft />
        </IconButton>
        <Box
          id="calendar-month-header"
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}
        >
          <Typography id="calendar-month-year" variant="h6" sx={{ fontWeight: 600 }}>
            {monthNames[currentMonth]} {currentYear}
          </Typography>
          {!isCurrentMonth && (
            <Button
              id="calendar-go-to-today-button"
              size="small"
              onClick={handleGoToToday}
              sx={{
                marginTop: 0.5,
                textTransform: 'none',
                fontSize: '0.75rem',
                color: colors.primary || '#1976d2',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                },
              }}
            >
              Go to today
            </Button>
          )}
        </Box>
        <IconButton
          id="calendar-next-month-button"
          onClick={handleNextMonth}
          size="small"
          disabled={!canGoNext}
          sx={{
            color: colors.text.primary,
            '&:hover:not(:disabled)': {
              backgroundColor: colors.background.secondary,
            },
            '&:disabled': {
              opacity: 0.4,
            },
          }}
          aria-label="Next month"
        >
          <ChevronRight />
        </IconButton>
      </Box>

      <Box
        id="calendar-grid"
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 0.5,
          marginBottom: 2,
        }}
      >
        {/* Day headers */}
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <Typography
            key={day}
            variant="caption"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              padding: 0.5,
              color: colors.text.secondary,
            }}
          >
            {day}
          </Typography>
        ))}

        {/* Calendar days */}
        {calendarDays.map((dayData, index) => {
          if (!dayData) {
            // eslint-disable-next-line react/no-array-index-key
            return <Box key={`empty-${index}`} />;
          }

          const { day, wordCount, isToday, dateKey } = dayData;
          const bgColor = getDayColor(wordCount);

          return (
            <Tooltip
              key={dateKey}
              title={
                wordCount > 0
                  ? `${wordCount} words${wordCount >= dailyWordGoal ? ' ✓ Goal met!' : ''}`
                  : 'No activity'
              }
              arrow
              slotProps={{
                tooltip: {
                  sx: {
                    backgroundColor: colors.tooltip.background,
                    color: colors.tooltip.color,
                    border: `1px solid ${colors.tooltip.border}`,
                  },
                },
                arrow: {
                  sx: {
                    color: colors.tooltip.background,
                  },
                },
              }}
            >
              <Box
                id={`calendar-day-${dateKey}`}
                sx={{
                  aspectRatio: '1',
                  backgroundColor: bgColor,
                  border: isToday ? `2px solid ${colors.primary || '#1976d2'}` : 'none',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  minHeight: '32px',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    zIndex: 1,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    color: (() => {
                      if (wordCount >= dailyWordGoal) return '#fff';
                      if (wordCount > 0) return '#000';
                      return colors.text.secondary;
                    })(),
                    fontWeight: isToday ? 600 : 400,
                  }}
                >
                  {day}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {/* Legend */}
      <Box id="calendar-legend" sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginTop: 3 }}>
        <Box id="legend-goal-met" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: '#4caf50',
              borderRadius: '2px',
            }}
          />
          <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
            Goal met
          </Typography>
        </Box>
        <Box id="legend-partial" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: '#ffc107',
              borderRadius: '2px',
            }}
          />
          <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
            Partial
          </Typography>
        </Box>
        <Box id="legend-no-activity" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: colors.background.secondary || '#e5e5e5',
              borderRadius: '2px',
            }}
          />
          <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
            No activity
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

ActivityCalendar.propTypes = {
  activity: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      wordCount: PropTypes.number,
    })
  ),
  dailyWordGoal: PropTypes.number,
};

ActivityCalendar.defaultProps = {
  activity: [],
  dailyWordGoal: 500,
};

export default ActivityCalendar;
